import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import config from '../config';
import useGroupCallSFU from '../hooks/useGroupCallSFU';
import useRingtone from '../hooks/useRingtone';
import MuteIcon from './icons/MuteIcon';
import '../styles/VideoCall.css';
import '../styles/AudioCall.css';
import EndCallIcon from './icons/EndCallIcon';
import { buildGroupCallMemberIds, getEligibleGroupMembers } from '../utils/groupCallMembers';
import { isPageUnloading } from '../utils/activeCallPersistence';

/**
 * Group video call UI. All media is relayed through the mediasoup SFU; this
 * component never creates an RTCPeerConnection of its own.
 */
const GroupVideoCall = ({
  user,
  selectedGroup,
  onEndCall,
  socket,
  autoStart = false,
  showLauncher = true,
  ignoreIncoming = false,
  joinRequest = null,
  onJoinRequestHandled,
}) => {
  const [joinedParticipants, setJoinedParticipants] = useState([]);
  const [participantNames, setParticipantNames] = useState({});
  const [waitingIncoming, setWaitingIncoming] = useState(null);
  const waitingIncomingRef = useRef(null);

  const call = useGroupCallSFU(socket, user, 'video', { restoreIncoming: !ignoreIncoming });

  const myId = useMemo(() => {
    const raw = user?.id || user?._id || user;
    return raw?.toString?.() ?? String(raw ?? '');
  }, [user]);

  const {
    isCallActive,
    isIncomingCall,
    callStatus,
    callerId,
    callerName,
    groupCallId,
    groupId,
    groupName,
    participants,
    acceptedParticipants,
    localStream,
    error,
    setError,
    callDuration,
    connectionQuality,
    isMuted,
    isCameraOff,
    callStartedAt,
    incomingRestoreNonce,
    flipCamera,
    toggleMute,
    toggleCamera,
    startGroupCall,
    acceptGroupCall,
    joinOngoingGroupCall,
    rejectGroupCall,
    endGroupCall,
    leaveCurrentCall,
    handleEndCall,
    handleParticipantLeft,
    formatDuration,
  } = call;

  const { playRingtone, stopRingtone, resetCallAccepted } = useRingtone();
  const localVideoRef = useRef(null);
  const remoteVideoRefs = useRef(new Map()); // Map<userId, HTMLVideoElement>

  // ---- Group call-history bookkeeping (observes the lifecycle only) ----
  const isInitiatorRef = useRef(false);
  const groupConnectedRef = useRef(false);
  const groupConnectedAtRef = useRef(null);
  const activeGroupCallIdRef = useRef(null);
  const activeGroupIdRef = useRef(null);
  const loggedGroupCallRef = useRef(null);
  const wasActiveRef = useRef(false);

  // ============================================================
  // Helpers
  // ============================================================

  const fetchUserName = async (id) => {
    try {
      const res = await axios.get(`${config.apiUrl}/api/auth/profile/${id}?requestingUserId=${myId}`);
      return res.data?.username || id;
    } catch (e) {
      return id;
    }
  };

  const fetchJoinedParticipants = async (targetCallId) => {
    try {
      if (!targetCallId) return;
      const res = await axios.get(`${config.apiUrl}/api/calls/group/${targetCallId}`);
      if (Array.isArray(res.data?.participants)) {
        setJoinedParticipants(res.data.participants.filter((p) => p.id !== myId));
      }
    } catch (e) {
      setJoinedParticipants([]);
    }
  };

  const getParticipantDisplayName = (participant) => {
    const uid = participant?.userId?.toString?.() || String(participant?.userId || '');
    if (!uid) return 'Participant';
    if (uid === myId) return `${user?.username || user?.name || 'You'} (You)`;
    return participantNames[uid] || uid;
  };

  const primeIncomingCall = (data) => {
    call.setCallerId(data.callerId || data.from);
    call.setGroupId(data.groupId);
    call.setGroupName(data.groupName);
    if (data.callerName) call.setCallerName(data.callerName);
    if (data.groupCallId) call.setGroupCallId(data.groupCallId);
    call.setCallStatus('incoming');
    call.setIsIncomingCall(true);
  };

  // ============================================================
  // Call actions
  // ============================================================

  const setWaitingIncomingSafe = (next) => {
    waitingIncomingRef.current = next;
    setWaitingIncoming(next);
  };

  const handleAcceptCall = async () => {
    stopRingtone();
    setWaitingIncomingSafe(null);
    try {
      await acceptGroupCall();
    } catch (err) {
      console.error('Error accepting group video call:', err);
    }
  };

  const handleRejectCall = () => {
    stopRingtone();
    setWaitingIncomingSafe(null);
    try {
      rejectGroupCall();
    } catch (err) {
      console.error('Error rejecting group video call:', err);
    }
  };

  const handleAcceptWaitingCall = async () => {
    const waiting = waitingIncomingRef.current;
    if (!waiting) return;
    stopRingtone();
    setWaitingIncomingSafe(null);
    try {
      leaveCurrentCall();
      await new Promise((resolve) => setTimeout(resolve, 200));
      primeIncomingCall(waiting);
      await acceptGroupCall();
    } catch (err) {
      console.error('Error switching to incoming group video call:', err);
    }
  };

  const handleRejectWaitingCall = () => {
    const waiting = waitingIncomingRef.current;
    stopRingtone();
    setWaitingIncomingSafe(null);
    if (!waiting) return;
    try {
      socket?.emit(
        'sfuGroupCallReject',
        {
          from: myId,
          to: waiting.callerId || waiting.from,
          groupCallId: waiting.groupCallId,
          callType: 'video',
        },
        () => {}
      );
    } catch (err) {
      console.error('Error rejecting waiting group video call:', err);
    }
  };

  const handleEndCallClick = () => {
    stopRingtone();
    endGroupCall();
    if (onEndCall) {
      onEndCall({
        groupId,
        groupName,
        type: 'video',
        direction: callStatus === 'incoming' ? 'incoming' : 'outgoing',
      });
    }
  };

  const startCallWithMemberIds = async (memberIds) => {
    // Only the initiator persists the call-history record. Capture the group id
    // now so switching chats later cannot redirect the log to another group.
    isInitiatorRef.current = true;
    groupConnectedRef.current = false;
    groupConnectedAtRef.current = null;
    activeGroupIdRef.current = selectedGroup?._id || selectedGroup?.id || null;
    await startGroupCall(selectedGroup, memberIds);
  };

  const handleStartCall = async () => {
    if (!selectedGroup) {
      setError('Please select a group to call');
      return;
    }

    // Every member of the group is rung; there is no participant limit.
    const eligible = getEligibleGroupMembers(selectedGroup, myId);
    if (eligible.length === 0) {
      setError('No members available to call in this group');
      return;
    }

    await startCallWithMemberIds(buildGroupCallMemberIds(myId, eligible.map((m) => m.id)));
  };

  // Latest values for the long-lived socket listener effect below.
  const callRefs = useRef({});
  callRefs.current = {
    ignoreIncoming,
    myId,
    callStatus,
    groupCallId,
    call,
    stopRingtone,
    resetCallAccepted,
    playRingtone,
    fetchUserName,
    primeIncomingCall,
    handleEndCall,
    handleParticipantLeft,
    setWaitingIncomingSafe,
    handleAcceptWaitingCall,
    handleAcceptCall,
  };

  // ============================================================
  // Group call lifecycle events
  // ============================================================

  useEffect(() => {
    if (!socket) return undefined;

    const onIncomingGroupCall = async (data) => {
      const r = callRefs.current;
      if (r.ignoreIncoming) return;
      if (data.from === r.myId) return;
      if (data.groupCallId && r.groupCallId && String(data.groupCallId) === String(r.groupCallId)) {
        return;
      }

      if (['connected', 'calling', 'connecting'].includes(r.callStatus)) {
        r.setWaitingIncomingSafe({
          callerId: data.callerId || data.from,
          from: data.from,
          groupId: data.groupId,
          groupName: data.groupName,
          callerName: data.callerName,
          groupCallId: data.groupCallId,
        });
        r.resetCallAccepted();
        r.playRingtone();
        if (!data.callerName && data.from) {
          const name = await r.fetchUserName(data.from);
          const current = waitingIncomingRef.current;
          if (current && String(current.groupCallId) === String(data.groupCallId)) {
            r.setWaitingIncomingSafe({ ...current, callerName: name });
          }
        }
        return;
      }

      r.call.setError(null);
      r.primeIncomingCall(data);
      r.resetCallAccepted();
      r.playRingtone();

      if (!data.callerName) {
        const name = await r.fetchUserName(data.from);
        r.call.setCallerName(name);
      }
    };

    const onGroupCallAccepted = (data) => {
      const r = callRefs.current;
      const acceptedUserId = data.from?.toString();
      if (!acceptedUserId || acceptedUserId === r.myId) return;
      if (r.callStatus === 'calling' || r.callStatus === 'connecting') {
        r.call.setCallStatus('connected');
        r.call.startCallTimer();
      }
      r.call.setAcceptedParticipants((prev) => {
        if (prev.has(acceptedUserId)) return prev;
        const next = new Set(prev);
        next.add(acceptedUserId);
        return next;
      });
    };

    const onGroupCallRejected = (data) => {
      console.log('Group video call rejected by:', data.from);
    };

    const onGroupCallEnded = (data) => {
      const r = callRefs.current;
      const endedId = data?.groupCallId?.toString();
      const currentId = r.groupCallId?.toString();
      if (endedId && currentId && endedId !== currentId) return;
      r.stopRingtone();
      r.setWaitingIncomingSafe?.(null);
      r.handleEndCall();
    };

    const onParticipantLeft = (data) => {
      const r = callRefs.current;
      const leftUserId = data?.userId;
      if (!leftUserId) return;
      const leftCallId = data?.groupCallId?.toString();
      const currentId = r.groupCallId?.toString();
      if (leftCallId && currentId && leftCallId !== currentId) return;
      if (leftUserId.toString() === r.myId) {
        r.handleEndCall();
        return;
      }
      r.handleParticipantLeft(leftUserId);
    };

    socket.on('incomingGroupVideoCall', onIncomingGroupCall);
    socket.on('groupVideoCallAccepted', onGroupCallAccepted);
    socket.on('groupVideoCallRejected', onGroupCallRejected);
    socket.on('groupVideoCallEnded', onGroupCallEnded);
    socket.on('groupVideoCallParticipantLeft', onParticipantLeft);

    return () => {
      socket.off('incomingGroupVideoCall', onIncomingGroupCall);
      socket.off('groupVideoCallAccepted', onGroupCallAccepted);
      socket.off('groupVideoCallRejected', onGroupCallRejected);
      socket.off('groupVideoCallEnded', onGroupCallEnded);
      socket.off('groupVideoCallParticipantLeft', onParticipantLeft);
    };
    // Handlers read everything dynamic through callRefs, so this stays
    // registered for the lifetime of the socket.
  }, [socket]);

  // ============================================================
  // Service worker (notification) messages
  // ============================================================

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;

    const handleMessage = (event) => {
      const { type, data } = event.data || {};
      if (!data || data.type !== 'group_call') return;
      if ((data.callType || 'audio') !== 'video') return;

      const r = callRefs.current;

      if (type === 'PLAY_RINGTONE') {
        r.resetCallAccepted();
        r.playRingtone();
        return;
      }
      if (type === 'STOP_RINGTONE') {
        r.stopRingtone();
        return;
      }

      if (type === 'GROUP_CALL_OPEN') {
        r.resetCallAccepted();
        r.primeIncomingCall(data);
        r.playRingtone();
        fetchJoinedParticipants(data.groupCallId);
        return;
      }

      if (type === 'GROUP_CALL_ACCEPT') {
        const busy = ['connected', 'calling', 'connecting'].includes(r.callStatus);
        if (busy) {
          r.setWaitingIncomingSafe({
            callerId: data.callerId || data.from,
            from: data.from,
            groupId: data.groupId,
            groupName: data.groupName,
            callerName: data.callerName,
            groupCallId: data.groupCallId,
          });
          setTimeout(() => r.handleAcceptWaitingCall(), 100);
          return;
        }
        r.primeIncomingCall(data);
        setTimeout(() => r.handleAcceptCall(), 100);
        return;
      }

      if (type === 'GROUP_CALL_REJECT') {
        r.primeIncomingCall(data);
        setTimeout(() => handleRejectCall(), 100);
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore the incoming-call screen when the app cold-starts from a notification.
  useEffect(() => {
    const restoreCallFromUrl = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const urlGroupCallId = params.get('groupCallId');
        if (!urlGroupCallId || params.get('callType') !== 'video') return;

        const urlGroupId = params.get('groupId');
        const urlCallerId = params.get('callerId');

        // Clear immediately so re-renders cannot re-trigger this.
        window.history.replaceState({}, '', window.location.pathname);

        let resolvedGroupName = params.get('groupName') || 'Group Call';
        if (!params.get('groupName') && urlGroupId) {
          try {
            const res = await axios.get(`${config.apiUrl}/api/groups/${urlGroupId}`);
            if (res.data?.name) resolvedGroupName = res.data.name;
          } catch (err) {
            console.warn('Could not fetch group name:', err);
          }
        }

        let resolvedCallerName = params.get('callerName') || urlCallerId;
        if (!params.get('callerName') && urlCallerId) {
          resolvedCallerName = await fetchUserName(urlCallerId);
        }

        callRefs.current.primeIncomingCall({
          callerId: urlCallerId,
          groupId: urlGroupId,
          groupName: resolvedGroupName,
          callerName: resolvedCallerName,
          groupCallId: urlGroupCallId,
        });
        playRingtone();
        fetchJoinedParticipants(urlGroupCallId);
      } catch (e) {
        console.error('Error restoring group video call from URL:', e);
      }
    };

    restoreCallFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isIncomingCall && groupCallId) fetchJoinedParticipants(groupCallId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIncomingCall, groupCallId]);

  // ============================================================
  // Media binding
  // ============================================================

  useEffect(() => {
    const videoEl = localVideoRef.current;
    if (!videoEl) return;

    if (localStream) {
      if (videoEl.srcObject !== localStream) videoEl.srcObject = localStream;
      videoEl.muted = true;
      videoEl.play().catch((err) => console.warn('Local video play error:', err));
    } else if (videoEl.srcObject) {
      videoEl.srcObject = null;
    }
  }, [localStream, callStatus]);

  useEffect(() => {
    participants.forEach(({ userId, stream }) => {
      if (!stream) return;

      let videoElement = remoteVideoRefs.current.get(userId);
      if (!videoElement) {
        videoElement = document.createElement('video');
        videoElement.autoplay = true;
        videoElement.playsInline = true;
        videoElement.muted = false;
        videoElement.style.cssText = 'width: 100%; height: 100%; object-fit: cover;';
        remoteVideoRefs.current.set(userId, videoElement);
      }

      // Audio and video arrive as separate consumers and are appended to the
      // same MediaStream. Chromium will not render a video track added after
      // srcObject was bound, so rebind whenever the track set changes —
      // otherwise remote tiles stay black while RTP is flowing.
      const signature = stream
        .getTracks()
        .map((t) => `${t.kind}:${t.id}`)
        .sort()
        .join('|');

      if (videoElement.srcObject !== stream || videoElement.__sfuTrackSig !== signature) {
        try {
          videoElement.srcObject = null;
        } catch (_) {}
        videoElement.srcObject = stream;
        videoElement.__sfuTrackSig = signature;
      }

      const played = videoElement.play();
      if (played?.catch) {
        played.catch((err) => console.warn(`Remote video play error for ${userId}:`, err?.message || err));
      }
    });

    const currentIds = new Set(participants.map((p) => p.userId));
    remoteVideoRefs.current.forEach((videoElement, userId) => {
      if (currentIds.has(userId)) return;
      videoElement.srcObject = null;
      videoElement.remove();
      remoteVideoRefs.current.delete(userId);
    });
  }, [participants]);

  // Route remote audio to the selected speaker where the browser supports it.
  useEffect(() => {
    const outputId = call.selectedAudioOutputId;
    if (!outputId) return;
    remoteVideoRefs.current.forEach((el) => {
      try {
        el?.setSinkId?.(outputId);
      } catch (_) {}
    });
  }, [call.selectedAudioOutputId, participants.length]);

  // Resolve display names for everyone on screen.
  useEffect(() => {
    let cancelled = false;
    const ids = Array.from(new Set(participants.map((p) => p.userId).filter(Boolean)));

    ids.forEach(async (uid) => {
      if (participantNames[uid]) return;
      const name = await fetchUserName(uid);
      if (!cancelled) setParticipantNames((prev) => (prev[uid] ? prev : { ...prev, [uid]: name }));
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participants]);

  // ============================================================
  // Ringtone + history
  // ============================================================

  useEffect(() => {
    if (callStatus === 'connecting' || callStatus === 'connected') stopRingtone();
  }, [callStatus, stopRingtone]);

  useEffect(() => {
    if (!isIncomingCall && callStatus !== 'incoming') stopRingtone();
  }, [isIncomingCall, callStatus, stopRingtone]);

  useEffect(() => {
    if (!incomingRestoreNonce) return;
    playRingtone();
  }, [incomingRestoreNonce, playRingtone]);

  useEffect(() => {
    if (groupCallId) activeGroupCallIdRef.current = groupCallId;
  }, [groupCallId]);

  useEffect(() => {
    if (callStatus === 'connected' && !groupConnectedRef.current) {
      groupConnectedRef.current = true;
      groupConnectedAtRef.current = callStartedAt || Date.now();
    }
  }, [callStatus, callStartedAt]);

  // Persist one history record per call, on the initiator only.
  useEffect(() => {
    const active = ['calling', 'connecting', 'connected'].includes(callStatus);
    if (active) {
      wasActiveRef.current = true;
      return;
    }
    if (!wasActiveRef.current) return;
    if (isPageUnloading()) return;
    wasActiveRef.current = false;

    if (isInitiatorRef.current) {
      const callId = activeGroupCallIdRef.current;
      const targetGroupId = activeGroupIdRef.current;
      if (callId && targetGroupId && socket && loggedGroupCallRef.current !== callId) {
        loggedGroupCallRef.current = callId;
        const answered = groupConnectedRef.current;
        const durationSeconds =
          answered && groupConnectedAtRef.current
            ? Math.max(0, Math.round((Date.now() - groupConnectedAtRef.current) / 1000))
            : 0;
        try {
          socket.emit('logGroupCallHistory', {
            callId,
            groupId: targetGroupId,
            initiatorId: myId,
            callType: 'video',
            status: answered ? 'answered' : 'missed',
            duration: durationSeconds,
          });
        } catch (err) {
          console.error('Failed to persist group call history:', err);
        }
      }
    }

    isInitiatorRef.current = false;
    groupConnectedRef.current = false;
    groupConnectedAtRef.current = null;
    activeGroupIdRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callStatus, socket, myId]);

  // Clean up detached video elements on unmount (the hook tears down media).
  useEffect(() => {
    const refs = remoteVideoRefs.current;
    return () => {
      refs.forEach((videoElement) => {
        videoElement.srcObject = null;
        videoElement.remove();
      });
      refs.clear();
    };
  }, []);

  useEffect(() => {
    if (
      autoStart &&
      selectedGroup &&
      !isCallActive &&
      !['calling', 'connected', 'connecting'].includes(callStatus)
    ) {
      handleStartCall();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, selectedGroup, isCallActive, callStatus]);

  useEffect(() => {
    if (!joinRequest?.groupCallId || (joinRequest.callType || 'video') !== 'video') return undefined;
    let cancelled = false;
    (async () => {
      try {
        await joinOngoingGroupCall(joinRequest);
      } catch (err) {
        console.error('Error joining ongoing group video call:', err);
      } finally {
        if (!cancelled) onJoinRequestHandled?.();
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinRequest?.groupCallId, joinRequest?.callType]);

  // ============================================================
  // Render
  // ============================================================

  const waitingBanner = waitingIncoming
    ? createPortal(
        <div className="call-waiting-banner" role="alertdialog" aria-label="Incoming group video call">
          <div className="call-waiting-info">
            <div className="call-waiting-title">Incoming group video call</div>
            <div className="call-waiting-name">
              {waitingIncoming.groupName || 'Group'}
              {waitingIncoming.callerName || waitingIncoming.callerId
                ? ` · ${waitingIncoming.callerName || waitingIncoming.callerId}`
                : ''}
            </div>
          </div>
          <div className="call-waiting-actions">
            <button
              type="button"
              className="accept-btn"
              onClick={handleAcceptWaitingCall}
              title="Leave current call and join"
            >
              Accept
            </button>
            <button
              type="button"
              className="reject-btn"
              onClick={handleRejectWaitingCall}
              title="Decline"
            >
              Reject
            </button>
          </div>
        </div>,
        document.body
      )
    : null;

  if (isIncomingCall) {
    return createPortal(
      <div className="video-call-overlay">
        <div className="video-call-modal incoming">
          <div className="call-header">
            <h3>Incoming Group Video Call</h3>
            <p>{groupName || 'Group'}</p>
            <p className="caller-name">{callerName || callerId || 'Unknown'}</p>
          </div>
          {joinedParticipants.length > 0 && (
            <div className="incoming-joined-info">
              <p className="incoming-joined-count">{joinedParticipants.length} already in call</p>
              <div className="incoming-joined-list">
                {joinedParticipants.slice(0, 8).map((p) => (
                  <div key={p.id} className="incoming-joined-item">
                    <div className="incoming-joined-avatar">
                      {p.profileImage ? (
                        <img src={p.profileImage} alt={p.username} className="incoming-joined-avatar-img" />
                      ) : (
                        (p.username || '?').toString().charAt(0).toUpperCase()
                      )}
                    </div>
                    <span className="incoming-joined-name">{p.username}</span>
                  </div>
                ))}
                {joinedParticipants.length > 8 && (
                  <span className="incoming-joined-more">+{joinedParticipants.length - 8} more</span>
                )}
              </div>
            </div>
          )}
          {error && <div className="call-error">{error}</div>}
          <div className="call-actions">
            <button className="accept-btn" onClick={handleAcceptCall}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
              </svg>
              Join
            </button>
            <button className="reject-btn" onClick={handleRejectCall}>
              <EndCallIcon size={24} />
              Ignore
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  if (callStatus === 'calling') {
    const acceptedCount = acceptedParticipants ? acceptedParticipants.size : 0;
    return createPortal(
      <div className="video-call-overlay">
        <div className="video-call-modal calling">
          <video ref={localVideoRef} autoPlay playsInline muted className="hidden-local-preview" />
          <div className="call-header">
            <h3>Calling Group...</h3>
            <p>{groupName || 'Group'}</p>
            {acceptedCount > 0 && (
              <p className="participants-count" style={{ fontSize: '14px', color: '#4CAF50', marginTop: '8px' }}>
                {acceptedCount} {acceptedCount === 1 ? 'member' : 'members'} joined
              </p>
            )}
          </div>
          {error && <div className="call-error">{error}</div>}
          <div className="call-status">
            <div className="calling-animation">
              <div className="pulse" />
              <div className="pulse" />
              <div className="pulse" />
            </div>
          </div>
          <div className="call-actions">
            <button className="end-call-btn" onClick={handleEndCallClick}>
              <EndCallIcon size={24} />
              End Call
            </button>
          </div>
        </div>
        {waitingBanner}
      </div>,
      document.body
    );
  }

  if (callStatus === 'connected' || callStatus === 'connecting') {
    const isFullyConnected = callStatus === 'connected';
    const totalParticipants = participants.length + 1;

    return createPortal(
      <div className="video-call-overlay" style={{ cursor: 'default' }}>
        <div className="video-call-container">
          <div className="video-grid" data-count={totalParticipants}>
            {participants.map(({ userId, stream, audioMuted, videoMuted }) => {
              const videoElement = remoteVideoRefs.current.get(userId);
              const quality = connectionQuality.get(userId);
              const displayName = getParticipantDisplayName({ userId });

              return (
                <div
                  key={userId}
                  className="video-grid-item"
                  ref={(container) => {
                    if (container && videoElement && !container.contains(videoElement)) {
                      container.appendChild(videoElement);
                    }
                  }}
                >
                  {(!stream || videoMuted) && (
                    <div className="video-tile-placeholder">
                      <div className="video-tile-avatar">
                        {(displayName || '?').toString().charAt(0).toUpperCase()}
                      </div>
                    </div>
                  )}
                  <ConnectionQualityIndicator quality={quality} />
                  {audioMuted && (
                    <span className="participant-mute-badge" title={`${displayName} is muted`}>
                      <MuteIcon muted size={14} />
                    </span>
                  )}
                  <span className="participant-label">{displayName}</span>
                </div>
              );
            })}

            <div className="video-grid-item local-tile">
              <video ref={localVideoRef} autoPlay playsInline muted />
              {isCameraOff && (
                <div className="video-tile-placeholder">
                  <div className="video-tile-avatar">
                    {(user?.username || 'You').toString().charAt(0).toUpperCase()}
                  </div>
                </div>
              )}
              <span className="participant-label">{getParticipantDisplayName({ userId: myId })}</span>
            </div>
          </div>

          <div className="video-call-controls">
            <button
              className={`control-btn ${isMuted ? 'active' : ''}`}
              onClick={toggleMute}
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              <MuteIcon muted={isMuted} size={20} />
            </button>

            <button
              className={`control-btn ${isCameraOff ? 'active' : ''}`}
              onClick={toggleCamera}
              title={isCameraOff ? 'Turn on camera' : 'Turn off camera'}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {isCameraOff ? (
                  <>
                    <path d="M1 1l22 22" />
                    <path d="M16 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4" />
                    <path d="M8 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4" />
                  </>
                ) : (
                  <>
                    <polygon points="23 7 16 12 23 17 23 7" />
                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                  </>
                )}
              </svg>
            </button>

            {!isCameraOff && (
              <button className="control-btn" onClick={flipCamera} title="Flip camera">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <path d="M15.5 12.5a3.5 3.5 0 0 1-5.8 2.2" />
                  <path d="M9.5 14.5l-1.2 1.2" />
                  <path d="M8.5 12.5a3.5 3.5 0 0 1 5.8-2.2" />
                  <path d="M14.5 10.5l1.2-1.2" />
                </svg>
              </button>
            )}

            <button className="control-btn end-call" onClick={handleEndCallClick}>
              <EndCallIcon size={20} />
            </button>
          </div>

          <div className="call-info">
            <h3>{groupName || 'Group Call'}</h3>
            <p className="participants-count">{totalParticipants} participants</p>
            <span className="call-duration">
              {isFullyConnected ? formatDuration(callDuration) : 'Connecting...'}
            </span>
            {error && <div className="call-error">{error}</div>}
          </div>
        </div>
        {waitingBanner}
      </div>,
      document.body
    );
  }

  if (!showLauncher) return null;

  return (
    <>
      <span
        className={`action-icon${!selectedGroup ? ' disabled' : ''}`}
        onClick={() => {
          if (selectedGroup) handleStartCall();
        }}
        title={selectedGroup ? 'Start group video call' : 'Select a group to call'}
        role="button"
        aria-label="Start group video call"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && selectedGroup) handleStartCall();
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polygon points="23 7 16 12 23 17 23 7" />
          <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
        </svg>
      </span>
    </>
  );
};

const ConnectionQualityIndicator = ({ quality }) => {
  if (!quality) return null;

  const isPoor = quality.packetsLost > 10 || quality.rtt > 500;
  const isFair = quality.packetsLost > 5 || quality.rtt > 300;
  const color = isPoor ? '#f44336' : isFair ? '#ff9800' : '#4caf50';
  const label = isPoor ? 'Poor' : isFair ? 'Fair' : 'Good';

  return (
    <div
      style={{
        position: 'absolute',
        top: '10px',
        left: '10px',
        backgroundColor: 'rgba(0,0,0,0.7)',
        color: 'white',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        zIndex: 5,
      }}
    >
      <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: color }} />
      <span>{label}</span>
    </div>
  );
};

export default GroupVideoCall;
